/**
 * useScrollRestoration — remembers the scroll offset of a grid/page the
 * instant a full-view overlay opens on top of it, then snaps back to that
 * exact offset the instant the overlay closes. No animation, no visible
 * jump-to-top-then-scroll-back, no flash.
 *
 * Symmetric with useBackButtonClose: pass the same `isOpen` boolean that
 * drives the overlay (e.g. `selectedWallpaper !== null`). Restoration runs
 * inside a `useLayoutEffect`, i.e. synchronously after the DOM commits but
 * before the browser paints the frame where the overlay unmounts — so the
 * user never sees the underlying grid render at the wrong offset first.
 *
 * One instance of this hook can cover every surface that shares the same
 * scroll container. In this app, Explore (all sections), Favorites, and
 * Library all render inside the same window-scrolled page and share the
 * same overlays (WallpaperViewer / RingtoneFullScreenPlayer) — so a single
 * `useScrollRestoration(isOpen)` call in HomePage covers all of them at
 * once. A modal that owns its own internal scroll container (rather than
 * the window) should pass `containerScrollAccessor(() => ref.current)`.
 *
 * Virtualized grids (VirtualWallpaperGrid / useWindowVirtualizer) in this
 * app use formula-estimated row heights (derived from measured container
 * width), not per-item async measurement — so the scrollable height is
 * already correct on mount and a plain scrollTo restores the right rows
 * without needing to wait for anything to "measure".
 */

import { useLayoutEffect, useRef } from 'react';

export interface ScrollAccessor {
  get: () => number;
  set: (offset: number) => void;
}

/** Reads/writes window.scrollY — use for pages/sections that scroll the document itself. */
export const windowScrollAccessor: ScrollAccessor = {
  get: () => window.scrollY,
  set: (offset) => window.scrollTo({ top: offset, left: 0, behavior: 'instant' as ScrollBehavior }),
};

/** Builds an accessor for an explicit scrollable container element (e.g. a modal's own scroll div). */
export function containerScrollAccessor(getEl: () => HTMLElement | null): ScrollAccessor {
  return {
    get: () => getEl()?.scrollTop ?? 0,
    set: (offset) => {
      const el = getEl();
      if (el) el.scrollTop = offset;
    },
  };
}

/**
 * @param isOpen    Whether the full-view overlay covering the grid is currently open.
 * @param accessor  How to read/write the scroll offset. Defaults to window scroll.
 */
export function useScrollRestoration(isOpen: boolean, accessor: ScrollAccessor = windowScrollAccessor): void {
  const savedRef = useRef<number | null>(null);

  // Keep a stable ref to the latest accessor so a new function reference
  // passed in on every render doesn't force this effect to re-run.
  const accessorRef = useRef(accessor);
  accessorRef.current = accessor;

  useLayoutEffect(() => {
    if (isOpen) {
      // Overlay just opened — remember exactly where the underlying grid was.
      if (savedRef.current === null) {
        savedRef.current = accessorRef.current.get();
      }
    } else if (savedRef.current !== null) {
      // Overlay just closed — snap back before the browser paints this frame.
      const pos = savedRef.current;
      savedRef.current = null;
      accessorRef.current.set(pos);
    }
  }, [isOpen]);
}
